import { useEffect, useState, useCallback } from "react";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";

// Progress ring config (button size is 48px => 3rem)
const RING_SIZE = 48;
const RING_STROKE = 3;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export default function ScrollButtons() {
  // Scroll progress in percentage (0 - 100)
  const [progress, setProgress] = useState(0);
  // Whether the page is actually scrollable
  const [scrollable, setScrollable] = useState(false);

  const updateScroll = useCallback(() => {
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop;
    const maxScroll = doc.scrollHeight - window.innerHeight;

    if (maxScroll > 50) {
      setScrollable(true);
      setProgress(Math.min(100, Math.max(0, (scrollTop / maxScroll) * 100)));
    } else {
      setScrollable(false);
      setProgress(0);
    }
  }, []);

  useEffect(() => {
    let ticking = false;

    // Throttle scroll updates using requestAnimationFrame
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    updateScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    // Page height changes when async content loads (API data, images, route change)
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(onScroll);
      observer.observe(document.body);
    }

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (observer) observer.disconnect();
    };
  }, [updateScroll]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const scrollToBottom = () =>
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });

  const showTop = scrollable && progress > 5;
  const showBottom = scrollable && progress < 95;

  const dashOffset = RING_CIRCUMFERENCE - (progress / 100) * RING_CIRCUMFERENCE;

  return (
    <div className="fixed bottom-6 right-5 z-50 flex flex-col items-center gap-3 sm:bottom-7 sm:right-7">
      {/* ================= SCROLL TO TOP ================= */}
      <div
        className={`group relative h-12 w-12 transition-all duration-300 ${
          showTop
            ? "translate-y-0 scale-100 opacity-100"
            : "pointer-events-none translate-y-4 scale-90 opacity-0"
        }`}
      >
        {/* Tooltip */}
        <span className="pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2 translate-x-2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100">
          Back to top
        </span>

        <button
          type="button"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          className="relative flex h-12 w-12 items-center justify-center rounded-full bg-[#DA2128] text-white shadow-xl shadow-[#DA2128]/35 transition-all duration-300 hover:-translate-y-1 hover:bg-[#DB6400] hover:shadow-[#DB6400]/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#DB6400] focus-visible:ring-offset-2"
        >
          {/* Progress ring */}
          <svg
            className="pointer-events-none absolute inset-0 -rotate-90"
            width={RING_SIZE}
            height={RING_SIZE}
            viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
            aria-hidden="true"
          >
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              stroke="rgba(255,255,255,0.25)"
              strokeWidth={RING_STROKE}
            />
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              stroke="#ffffff"
              strokeWidth={RING_STROKE}
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              style={{ transition: "stroke-dashoffset 120ms linear" }}
            />
          </svg>

          <FaArrowUp className="relative h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5" />
        </button>
      </div>

      {/* ================= SCROLL TO BOTTOM ================= */}
      <div
        className={`group relative h-12 w-12 transition-all duration-300 ${
          showBottom
            ? "translate-y-0 scale-100 opacity-100"
            : "pointer-events-none -translate-y-4 scale-90 opacity-0"
        }`}
      >
        {/* Tooltip */}
        <span className="pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2 translate-x-2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100">
          Go to bottom
        </span>

        <button
          type="button"
          onClick={scrollToBottom}
          aria-label="Scroll to bottom"
          className="flex h-12 w-12 items-center justify-center rounded-full border border-[#DA2128]/20 bg-white text-[#DA2128] shadow-xl shadow-black/15 transition-all duration-300 hover:translate-y-1 hover:border-[#DB6400] hover:bg-[#DB6400] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#DB6400] focus-visible:ring-offset-2"
        >
          <FaArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
        </button>
      </div>
    </div>
  );
}